import { Box, Text } from '@landtrustinc/design-system';
import React, { useCallback } from 'react';

import type { WebSocketMessage } from '../../types';

type MockMessageOption = {
  label: string;
  value: string;
  getMessage: () => WebSocketMessage;
};

const MOCK_MESSAGE_OPTIONS: MockMessageOption[] = [
  {
    label: 'Signup Required',
    value: 'signup_cta',
    getMessage: () => ({
      type: 'signup_cta',
      messageId: `mock-signup-${Date.now()}`,
      data: {
        message: 'Want to keep chatting with Buck?',
        variant: 'default',
        content: 'Create a free account to save this property and view it later on any device.',
        ctaLabel: 'Free Sign Up',
      },
    }),
  },
  {
    label: 'LT+ CTA',
    value: 'lt_plus_cta',
    getMessage: () => ({
      type: 'lt_plus_cta',
      messageId: `mock-ltplus-${Date.now()}`,
      data: {
        variant: 'draw-protection',
      },
    }),
  },
];

type MockSelectorProps = {
  onInjectMessage: (wsMessage: WebSocketMessage) => void;
};

const MockSelector: React.FC<MockSelectorProps> = ({ onInjectMessage }) => {
  const availableOptions = MOCK_MESSAGE_OPTIONS;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = e.target.value;
      if (!selectedValue) return;

      const option = availableOptions.find((opt) => opt.value === selectedValue);
      if (option) {
        // For lt_plus_cta, we need to first inject a complete assistant message, then the CTA
        if (option.value.startsWith('lt_plus_') || option.value === 'signup_cta') {
          const baseMessageId = `mock-base-${Date.now()}`;
          // First initialize the message buffer
          onInjectMessage({
            type: 'message',
            messageId: baseMessageId,
          });
          // Add content via chunk
          onInjectMessage({
            type: 'chunk',
            messageId: baseMessageId,
            data: 'This is a simulated assistant response to test the LT+ CTA.',
          });
          // Complete the message
          onInjectMessage({
            type: 'complete',
            messageId: baseMessageId,
          });
          // Then inject the LT+ CTA (with a small delay to ensure order)
          setTimeout(() => {
            onInjectMessage(option.getMessage());
          }, 50);
        } else {
          onInjectMessage(option.getMessage());
        }
      }

      // Reset select to placeholder
      e.target.value = '';
    },
    [availableOptions, onInjectMessage]
  );

  if (availableOptions.length === 0) {
    return null;
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="var(--spacing-2)"
      p="var(--spacing-2)"
      borderRadius="var(--radius-md)"
      mb="var(--spacing-2)"
    >
      <Text size="xs" fontWeight="semibold" color="var(--text-secondary)">
        Mock:
      </Text>
      <select onChange={handleChange} defaultValue="">
        <option value="" disabled>
          Select mock message...
        </option>
        {availableOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Box>
  );
};

export default MockSelector;
